<template>
    <div>
        <div class="flex justify-center my-8">
            <steps :current-step="step"
                   :steps="[
                            { name: 'Billing' },
                            { name: 'Payment' },
                            { name: 'Complete' }
                   ]"
            />
        </div>

        <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
            <div class="grid grid-cols-5 gap-10">
                <div class="col-span-3">
                    <form @submit.prevent="submitBillingForm"
                          v-if="step === 0"
                    >
                        <div class="grid grid-cols-2 gap-6">
                            <form-select v-model="form.membership_plan"
                                         label="Membership Plan"
                                         :options="plans"
                                         :track-as-integer="true"
                                         required
                                         :error="getErrorFor('membership_plan')"
                            ></form-select>

                            <form-select v-model="form.payment_type"
                                         label="Payment Type"
                                         :options="[
                                             { name: 'Monthly', value: 'monthly' },
                                             { name: 'Yearly', value: 'yearly' },
                                         ]"
                                         track-by="value"
                                         required
                                         :error="getErrorFor('payment_type')"
                            ></form-select>

                            <form-input v-model="form.first_name"
                                        label="First Name"
                                        required
                                        :error="getErrorFor('first_name')"
                            ></form-input>

                            <form-input v-model="form.last_name"
                                        label="Last Name"
                                        required
                                        :error="getErrorFor('last_name')"
                            ></form-input>

                            <form-input v-model="form.email"
                                        type="email"
                                        label="Email"
                                        required
                                        :error="getErrorFor('email')"
                            ></form-input>

                            <form-input v-model="form.phone"
                                        type="number"
                                        label="Phone"
                                        required
                                        :error="getErrorFor('phone')"
                            ></form-input>

                            <form-input v-model="form.address_1"
                                        label="Address 1"
                                        required
                                        :error="getErrorFor('address_1')"
                            ></form-input>

                            <form-input v-model="form.address_2"
                                        label="Address 2"
                                        :error="getErrorFor('address_2')"
                            ></form-input>

                            <form-input v-model="form.town"
                                        label="Town / City"
                                        class="col-span-2"
                                        required
                                        :error="getErrorFor('town')"
                            ></form-input>

                            <form-input v-model="form.county"
                                        label="County"
                                        required
                                        :error="getErrorFor('county')"
                            ></form-input>

                            <form-input v-model="form.post_code"
                                        label="Post Code"
                                        required
                                        :error="getErrorFor('post_code')"
                            ></form-input>

                            <form-select v-model="form.country"
                                         label="Country"
                                         :options="countries"
                                         track-by="value"
                                         class="col-span-2"
                                         required
                                         :error="getErrorFor('country')"
                            ></form-select>

                            <form-textarea v-model="form.notes"
                                           label="Notes"
                                           class="col-span-2"
                                           :error="getErrorFor('notes')"
                            ></form-textarea>
                        </div>

                        <div class="flex justify-end mt-6">
                            <form-button type="submit">
                                Continue
                            </form-button>
                        </div>
                    </form>

                    <form @submit.prevent="submitPaymentForm"
                          v-else
                    >
                        <payment-options v-model="form.payment_method"></payment-options>

                        <h2>Vouchers</h2>
                        <p>
                            If you have a voucher code, please enter it below to apply it to your booking.
                        </p>

                        <form-voucher @voucher-applied="credit = $event"></form-voucher>

                        <h2 class="mt-10">Contact Preferences & Terms</h2>

                        <div class="font-semibold text-gray-600 mt-4">
                            <div class="flex items-center">
                                <form-toggle v-model="form.marketing"></form-toggle>
                                <div class="ml-2">
                                    Contact me with latest news and offers
                                </div>
                            </div>

                            <div class="flex items-center mt-2">
                                <form-toggle v-model="form.adult"></form-toggle>
                                <div class="ml-2">
                                    I confirm that I am at least 18 years of age
                                </div>
                            </div>

                            <div class="ml-12 mt-1 text-xs text-red-600" v-if="hasError('adult')">
                                {{ getErrorFor('adult') }}
                            </div>

                            <div class="flex items-center mt-2">
                                <form-toggle v-model="form.terms"></form-toggle>
                                <div class="ml-2">
                                    I have read and agree to the <a href="/terms-and-conditions" target="_blank" class="text-primary-600 hover:underline">terms and conditions</a>
                                </div>
                            </div>

                            <div class="ml-12 mt-1 text-xs text-red-600" v-if="hasError('terms')">
                                {{ getErrorFor('terms') }}
                            </div>

                        </div>

                        <div class="flex justify-end mt-6">
                            <form-button type="submit">
                                Continue
                            </form-button>
                        </div>
                    </form>
                </div>
                <div class="col-span-2">
                    <membership-basket :plans="plans"
                                       :selected-plan-id="form.membership_plan"
                                       :membership-type="this.form.payment_type"
                                       :customer-credit="credit"
                    ></membership-basket>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import FormInput from "../../../components/form/Input";
    import FormSelect from "../../../components/form/Select";
    import FormTextarea from "../../../components/form/Textarea";
    import FormButton from "../../../components/form/Button";
    import PaymentOptions from "../../../components/PaymentOptions";
    import FormToggle from "../../../components/form/public/Toggle";
    import FormVoucher from "../../../components/form/Voucher";

    import MembershipBasket from "./Basket";
    import Steps from "../../../components/Steps";

    import Form from "../../../mixins/Form";
    import Countries from "../../../mixins/Countries";

	export default {
	    data() {
	        return {
	            step: 0,

	            form: {
	                membership_plan: null,
                    payment_type: 'monthly',
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone: '',
                    address_1: '',
                    address_2: '',
                    town: '',
                    county: '',
                    post_code: '',
                    country: 'United Kingdom',
                    notes: '',
                    billing_hash: '',
                    payment_method: null,
                    marketing: false,
                    adult: false,
                    terms: false,
                },

                credit: '0.00',
            }
        },

        props: {
            preSelectedPlan: {
                type: Number
            },

            plans: {
                type: Array,
                required: true,
            },

            customerCredit: {
                type: String,
                required: true
            }
        },

        watch: {
            'form.first_name': function (value) {
                this.form.first_name = _.upperFirst(value);
            },

            'form.last_name': function (value) {
                this.form.last_name = _.upperFirst(value);
            },

            'form.address_1': function (value) {
                this.form.address_1 = _.upperFirst(value);
            },

            'form.address_2': function (value) {
                this.form.address_2 = _.upperFirst(value);
            },

            'form.town': function (value) {
                this.form.town = _.upperFirst(value);
            },

            'form.post_code': function (value) {
                this.form.post_code = value.toUpperCase();
            }
        },

        components: {
	        FormInput,
            FormSelect,
            FormTextarea,
            FormButton,
            MembershipBasket,
            Steps,
            PaymentOptions,
            FormToggle,
            FormVoucher,
        },

        mixins: [Form, Countries],

        created() {
            this.form.membership_plan = this.preSelectedPlan ?
                this.preSelectedPlan : _.first(this.plans).id;

            this.credit = this.customerCredit;
        },

        methods: {
	        submitBillingForm() {
	            this.post(route('customer.membership.billing.validate').url())
                    .then(response => {
                        this.form.billing_hash = response.data;

                        this.form.payment_method = 'card';
                        this.step = 1;
                    })
            },

            submitPaymentForm() {
	            this.post(route('customer.membership.payment').url())
                    .then(response => {
                        if (response.status === 200) {
                            return window.location = response.data;
                        } else if (response.status === 202) {
                            stripe.redirectToCheckout({
                                sessionId: response.data
                            }).then(function (result) {
                                // If `redirectToCheckout` fails due to a browser or network
                                // error, display the localized error message to your customer
                                // using `result.error.message`.
                            });
                        }
                    })
                    .catch(() => {
                        this.loading = false;
                    })
            }
        }
    }
</script>
